import { useCallback, useEffect, useState } from 'react';
import {
  fetchSubscriptionPlans,
  annualPrice,
  createSubscriptionPlan,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
  formatPrice,
  parseMoneyInput,
  type SubscriptionPlan,
  type SubscriptionPlanInput,
} from '@/lib/subscriptionPlans';

// ─────────────────────────────────────────────────────────────
// ZIFEK — SuperAdmin · Formules d'abonnement
// Le SuperAdmin fixe les prix (DHS), les mois gratuits et les
// avantages de chaque formule, y compris une formule « sur devis »
// (contacter le service client Zifek).
// ─────────────────────────────────────────────────────────────

const EMPTY_FORM: SubscriptionPlanInput = {
  nom: '',
  prix: 35,
  prixAnnuel: null,
  description: '',
  moisGratuits: 0,
  actif: true,
  ordre: 0,
  features: [],
  personnalise: false,
};

export default function SuperAdminPlansPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null);
  const [form, setForm] = useState<SubscriptionPlanInput>(EMPTY_FORM);
  // Saisie libre des prix : accepte « 0,5 » comme « 0.5 » (l'input number
  // refusait la virgule, d'où l'échec d'enregistrement d'une formule à 0,5).
  const [prixText, setPrixText] = useState('');
  const [prixAnnuelText, setPrixAnnuelText] = useState('');
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleting, setDeleting] = useState<SubscriptionPlan | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setPlans(await fetchSubscriptionPlans());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les formules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, ordre: plans.length + 1 });
    setPrixText(String(EMPTY_FORM.prix));
    setPrixAnnuelText('');
    setFeaturesText('');
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (p: SubscriptionPlan) => {
    setEditing(p);
    setForm({
      nom: p.nom,
      prix: p.prix,
      prixAnnuel: p.prixAnnuel,
      description: p.description,
      moisGratuits: p.moisGratuits,
      actif: p.actif,
      ordre: p.ordre,
      features: p.features,
      personnalise: p.personnalise,
    });
    setFeaturesText(p.features.join('\n'));
    setPrixText(formatPrice(p.prix));
    setPrixAnnuelText(p.prixAnnuel != null && Number(p.prixAnnuel) > 0 ? formatPrice(p.prixAnnuel) : '');
    setFormError('');
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.nom.trim()) {
      setFormError('Le nom de la formule est obligatoire.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const features = featuresText
        .split('\n')
        .map((f) => f.trim())
        .filter(Boolean);
      const payload: SubscriptionPlanInput = {
        ...form,
        prix: parseMoneyInput(prixText),
        prixAnnuel: prixAnnuelText.trim() ? parseMoneyInput(prixAnnuelText) : null,
        features,
      };
      if (editing) await updateSubscriptionPlan(editing.id, payload);
      else await createSubscriptionPlan(payload);
      setFormOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const target = deleting;
    setDeleting(null);
    setPlans((prev) => prev.filter((p) => p.id !== target.id));
    try {
      await deleteSubscriptionPlan(target.id);
    } catch {
      setError('Impossible de supprimer cette formule.');
      await load();
    }
  };

  const activeCount = plans.filter((p) => p.actif).length;

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Formules d&apos;abonnement</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {plans.length} formule(s) · {activeCount} active(s). Prix, mois gratuits et avantages sont modifiables.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors w-fit"
        >
          <i className="ri-add-line"></i>
          Nouvelle formule
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-price-tag-3-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucune formule. Créez la première.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`bg-background-50 border rounded-lg p-5 flex flex-col ${p.actif ? 'border-background-200/70' : 'border-background-200/70 opacity-60'}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{p.nom}</h3>
                  {p.description && <p className="text-xs text-foreground-500 line-clamp-1 mt-0.5">{p.description}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {p.personnalise ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-accent-100 text-accent-900 whitespace-nowrap">Sur devis</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-primary-100 text-primary-700 whitespace-nowrap">
                      {formatPrice(p.prix)} DHS / mois
                    </span>
                  )}
                  {!p.personnalise && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
                      {formatPrice(annualPrice(p))} DHS / an
                    </span>
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 whitespace-nowrap ${p.actif ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                    <i className={p.actif ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    {p.actif ? 'Active' : 'Masquée'}
                  </span>
                </div>
              </div>

              <div className="text-xs text-foreground-500 space-y-1 mb-4">
                <div className="flex items-center gap-1.5">
                  <i className="ri-gift-line text-foreground-400"></i>
                  {p.moisGratuits > 0 ? `${p.moisGratuits} mois gratuit${p.moisGratuits > 1 ? 's' : ''}` : 'Aucun mois gratuit'}
                </div>
                {p.features.length > 0 && (
                  <ul className="space-y-0.5 mt-1.5">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <i className="ri-check-line text-accent-500 mt-0.5"></i>
                        <span className="line-clamp-1">{f}</span>
                      </li>
                    ))}
                    {p.features.length > 4 && <li className="text-foreground-400">+{p.features.length - 4} autre(s)</li>}
                  </ul>
                )}
              </div>

              <div className="mt-auto flex items-center gap-2">
                <button
                  onClick={() => openEdit(p)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
                >
                  <i className="ri-pencil-line"></i>
                  Modifier
                </button>
                <button
                  onClick={() => setDeleting(p)}
                  title="Supprimer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-4 text-[11px] text-foreground-400 flex items-start gap-1.5 leading-relaxed">
        <i className="ri-information-line mt-0.5"></i>
        Ces formules s&apos;affichent sur la page publique <strong>/abonnement</strong>. Les prix sont exprimés en DHS (MAD) ; ils sont convertis dans la devise de chaque commerce au moment du paiement. L&apos;annuel utilise le prix annuel défini, ou 10 × le mensuel (2 mois offerts).
      </p>

      {/* Formulaire */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-950">
                {editing ? 'Modifier la formule' : 'Nouvelle formule'}
              </h3>
              <button
                onClick={() => setFormOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Nom</label>
                <input
                  type="text"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  placeholder="ex. Pro"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Prix (DHS / mois)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={prixText}
                    onChange={(e) => {
                      setPrixText(e.target.value);
                      setForm({ ...form, prix: parseMoneyInput(e.target.value) });
                    }}
                    disabled={form.personnalise}
                    placeholder="ex. 0,5"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 disabled:opacity-50"
                  />
                  <p className="text-[11px] text-foreground-400 mt-1">Décimales acceptées (0,5 ou 0.5).</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Mois gratuits</label>
                  <input
                    type="number"
                    min={0}
                    value={form.moisGratuits}
                    onChange={(e) => setForm({ ...form, moisGratuits: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Prix annuel (DHS / an)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={prixAnnuelText}
                  onChange={(e) => setPrixAnnuelText(e.target.value)}
                  disabled={form.personnalise}
                  placeholder={`Auto — ${formatPrice(parseMoneyInput(prixText) * 10)} DHS`}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 disabled:opacity-50"
                />
                <p className="text-[11px] text-foreground-400 mt-1">Laisser vide pour appliquer 10 × le prix mensuel (2 mois offerts).</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Description courte</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="ex. Pour les commerces qui grandissent"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Avantages (un par ligne)</label>
                <textarea
                  value={featuresText}
                  onChange={(e) => setFeaturesText(e.target.value)}
                  rows={4}
                  placeholder={'1 boutique en ligne\nProduits illimités\nPaiement en ligne'}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ordre d&apos;affichage</label>
                <input
                  type="number"
                  value={form.ordre}
                  onChange={(e) => setForm({ ...form, ordre: Number(e.target.value) })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-foreground-900">Formule « sur devis »</div>
                  <div className="text-[11px] text-foreground-500">Le client est invité à contacter le service client Zifek.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, personnalise: !form.personnalise })}
                  className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${form.personnalise ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'}`}
                  aria-label="Formule sur devis"
                >
                  <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
                </button>
              </div>

              <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-foreground-900">Formule active</div>
                  <div className="text-[11px] text-foreground-500">Si désactivée, elle n&apos;apparaît plus sur /abonnement.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, actif: !form.actif })}
                  className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors cursor-pointer flex-shrink-0 ${form.actif ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'}`}
                  aria-label="Activer la formule"
                >
                  <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
                </button>
              </div>

              {formError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation suppression */}
      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleting(null)} />
          <div className="relative w-full max-w-sm bg-background-50 border border-background-200/70 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer la formule ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">Action définitive.</p>
              </div>
            </div>
            <p className="text-xs text-foreground-600 mb-5">
              <strong>{deleting.nom}</strong> sera définitivement supprimée.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}